import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { CancelIcon, EditIcon, SubmitIcon, ViewIcon } from 'src/v3/core/icons'

import axios, { pluckData } from 'src/utilities/http-client'
import theme from 'src/theme'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CourseList } from '../../components/entity-list'
import {
  selectActiveLinkedService,
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
  fetchTrainingSeries,
  selectTrainingSeriesStatus,
  selectTrainingSeriesById,
  selectCoursesByTrainingSeriesID,
} from '../../manage.slice'

let updateTrainingSeries = (data: {
  id: string
  name: string
  description?: string
  courseIDs: string[]
  linkedServiceID: string
}) => axios.put<unknown>('/api/v2/manage/training-series', data).then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { trainingSeriesId } = useParams()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let trainingSeries = useAppSelector(
    selectTrainingSeriesById(trainingSeriesId),
  )
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let trainingSeriesCourses = useAppSelector(
    selectCoursesByTrainingSeriesID(trainingSeriesId!),
  )

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let [trainingSeriesName, setTrainingSeriesName] = useState<string>(
    trainingSeries?.name || '',
  )
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [editing, setEditing] = useState<boolean>(false)
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (trainingSeriesStatus === 'uninitialized') {
      dispatch(fetchTrainingSeries())
    }
  }, [trainingSeriesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  if (activeLinkedService !== trainingSeries?.linkedServiceID) {
    navigate('/manage/assignments/training-series')
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        maxWidth: 480,
        bgcolor: 'background.paper',
        margin: 'auto',
      }}
    >
      <div className="flex justify-center items-center my-2">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          {editing ? <EditIcon /> : <ViewIcon />}
        </Avatar>
        <Typography
          className="pl-2"
          variant="h4"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          {trainingSeries?.name || 'Unnamed Training Series'}
        </Typography>
      </div>
      <Divider className="my-3" />
      <Typography variant="h5" textAlign="center">
        Training Series Name
      </Typography>
      <TextField
        variant="outlined"
        className="mt-2 mb-4"
        label="Name"
        name="name"
        type="text"
        fullWidth
        value={trainingSeriesName}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setTrainingSeriesName(event.target.value)
        }}
        disabled={!editing}
      />
      <Typography variant="h5" textAlign="center">
        Courses
      </Typography>
      <CourseList
        courses={editing ? courses : trainingSeriesCourses}
        checkbox={
          editing
            ? {
                onClick: setCheckedCourses,
                checked: checkedCourses,
              }
            : undefined
        }
      />
      {editing ? (
        <div className="mt-4 flex justify-center">
          {saving ? (
            <CircularProgress />
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SubmitIcon />}
                style={{ marginRight: 5 }}
                disabled={!trainingSeriesName || !checkedCourses.length}
                onClick={() => {
                  setSaving(true)
                  updateTrainingSeries({
                    linkedServiceID: activeLinkedService,
                    id: trainingSeriesId!,
                    name: trainingSeriesName,
                    courseIDs: checkedCourses,
                  })
                    .then(() => dispatch(fetchTrainingSeries()))
                    .then(() => setEditing(false))
                    .then(() => setSaving(false))
                    .catch((err) => {
                      setErrorMessage(err?.response?.data)
                      setSaving(false)
                    })
                }}
              >
                Update
              </Button>
              <Snackbar
                open={!!errorMessage}
                onClose={() => setErrorMessage('')}
              >
                <Alert
                  variant="filled"
                  onClose={() => setErrorMessage('')}
                  severity="error"
                >
                  <strong>Error Updating Training Series: </strong>
                  {errorMessage}
                </Alert>
              </Snackbar>
              <Button
                variant="contained"
                size="large"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setTrainingSeriesName(trainingSeries?.name || '')
                  setCheckedCourses(trainingSeries?.courseIDs || [])
                  setEditing(!editing)
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="mt-4 flex justify-center">
          <Button
            variant="contained"
            size="large"
            startIcon={<EditIcon />}
            onClick={() => {
              setEditing(!editing)
              if (!checkedCourses.length) {
                setCheckedCourses(trainingSeries?.courseIDs || [])
              }
            }}
          >
            Edit
          </Button>
        </div>
      )}
    </Card>
  )
}
